import React, { useMemo, useRef, useState } from "react";
import { Row, Col, Space } from "antd";
import { CA_DD_MODULES, OptionValues, RevstackCASectionIds, RevstackDashboardModules, RevstackFilterTypes, WidgetConfig, WidgetData } from "@ts/common-types";
import WidgetCard from "./WidgetCard";
import { buildWidgetCardFilters } from "@/ts/revstack/ui-utils";
import { keys, values } from "underscore";
import { getRevstackWidgetData } from "@/ts/retrieve-data";
import {  getRevstackSelectedFilterOptions } from "@/ts/revstack/filters";
import { trendsClickHandler } from "@/ts/revstack/constants";

interface IRevstackGenericParentContainer {
    widgetData: WidgetData;
    widgetConfig: {
        icon: React.ReactNode;
        widgets: WidgetConfig[][];
        formatter: (widgetConfig: WidgetConfig, widget: WidgetConfig, widgetData: WidgetData) => any;
        config: {
            trendsFilters: any[];
            showRegionSelect: boolean;
            showCategorySelect: boolean;
            hideTrendsTabSection: boolean;
            defaultTab: CA_DD_MODULES;
            module: RevstackDashboardModules;
            trendsBtnSectionId: RevstackCASectionIds;
            layout: Record<string, any>;
            isTrendsButtonRequired?: boolean;
            isTrendsButtonComingSoon: any;
            isDeepDiveButtonRequired?: boolean;
            isDrillDownButtonRequired?: boolean;
            isBodyPaddingReq?: boolean;
            componentFilterType?: string;
            relatedComponentDataKeys?: string[];
            trendsFiltersConfig?: any;
        };
    };
    title: string;
}

//! Note: We would want ot use this component whever possible for rendering the widgets
//! This can be our supoer compone for parent have to plan accordingly...

const CardHeaderSection: React.FC<{
    title: string;
    titleIcon: React.ReactNode;
}> = React.memo(({ title, titleIcon }) => (
    <div className="d-flex gap-1 align-items-center w-100">
        <Space align="center" direction="horizontal" size={12}>
            <div className="icon-btn">{titleIcon}</div>
            <Space align="center" direction="horizontal" size={12}>
                <h4 className="mb-0">{title || "Performance Table"}</h4>
            </Space>
        </Space>
    </div>
));

const RevstackGenericParentContainer: React.FC<
    IRevstackGenericParentContainer
> = ({ widgetConfig, widgetData, title }) => {
    const [parentFilterValues, setParentFilterValues] = useState<
        Record<string, string | string[]>
    >({});
    const previousValueOnOpen = useRef<string | string[]>(null);
    const selectedFilterOptions = getRevstackSelectedFilterOptions();

    const parentFilters = useMemo(() => {
        const filters: any[] = [];
        
        if (widgetConfig?.widgets) {
            widgetConfig.widgets.forEach((widget: any) => {
                const formatterOutput = widget.formatter(widgetConfig, widget, widgetData);
                        
                        if (formatterOutput.parentFilters && formatterOutput.parentFilters.length > 0) {
                            formatterOutput.parentFilters.forEach((filter: any) => {
                                const currentValue = parentFilterValues[filter.dataKey] || filter.defaultFilter;
                                let validCurrentValue;
                                const isMultiSelect = filter.type === RevstackFilterTypes.MULTI_SELECT;
                                if (isMultiSelect) {
                                    const defaultValue = Array.isArray(filter.defaultFilter) 
                                        ? filter.defaultFilter 
                                        : filter.defaultFilter 
                                            ? [filter.defaultFilter] 
                                            : [filter.filterOptions?.[0]?.value].filter(Boolean);
                                    
                                    validCurrentValue = Array.isArray(currentValue) 
                                        ? currentValue 
                                        : currentValue 
                                            ? [currentValue] 
                                            : defaultValue;
                                    
                                    validCurrentValue = validCurrentValue.filter((value: string) => 
                                        filter.filterOptions?.find((opt: any) => opt.value === value)
                                    );
                                    
                                    if (validCurrentValue.length === 0) {
                                        validCurrentValue = defaultValue;
                                    }
                                } else {
                                    const defaultValue = filter.defaultFilter || filter.filterOptions?.[0]?.value;
                                    validCurrentValue = filter.filterOptions?.find((opt: any) => opt.value === currentValue) 
                                        ? currentValue 
                                        : defaultValue;
                                }
                                
                                const filterType = filter.type;
                                
                                const commonProps = {
                                    options: filter.filterOptions || [],
                                    width: filter.width || 180,
                                    placeholder: filter.placeholder || `Select ${filter.dataKey}`,
                                };

                                const actions = filter?.actions || {};

                                let onChange = (value: string[]) => {
                                    setParentFilterValues(prev => ({
                                        ...prev,
                                        [filter.dataKey]: value
                                    }));
                                };

                                let onOpenChange = (open: boolean) => {};

                                //TODO: Apply these edge cases onChange also if required same logic...
                                if (actions?.onOpenChange === "get-data") {
                                    onOpenChange = async (open: boolean) => {
                                        if (open) {
                                            previousValueOnOpen.current = validCurrentValue;
                                        } else {
                                            // Edge case 1: When options have ALL and +1 filter option available, don't trigger API call
                                            const hasAllOption = filter.filterOptions?.some((opt: any) =>
                                                opt.value === OptionValues.ALL
                                            );
                                            
                                            const totalOptions = filter.filterOptions?.length || 0;
                                            const hasAllAndOneMore = hasAllOption && totalOptions === 2;
                                            
                                            // Edge case 2: If current selection is not changed during dropdown interaction, don't trigger API call
                                            const isSelectionUnchanged = JSON.stringify(validCurrentValue) === JSON.stringify(previousValueOnOpen.current);

                                            if (hasAllAndOneMore || isSelectionUnchanged) {
                                                return;
                                            }

                                            await getRevstackWidgetData(
                                                actions?.route,
                                                widgetConfig,
                                                widgetConfig?.config?.componentFilterType,
                                                validCurrentValue,
                                                widgetConfig?.config?.relatedComponentDataKeys || []
                                            );
                                        } 
                                    }
                                }
                                
                                const filterConfig = {
                                    section: "" as const,
                                    type: filterType,
                                    actions,
                                    props: {
                                        ...commonProps,
                                        value: validCurrentValue,
                                        ...(filterType === RevstackFilterTypes.MULTI_SELECT && {
                                            onChange,
                                            onOpenChange,
                                            showAllOption: (() => {
                                                if (filter.showAllOption === false) return false;
                                                
                                                const allOptionValue = filter.allOptionValue || OptionValues.ALL;
                                                const hasExistingAllOption = filter.filterOptions?.some((opt: any) => 
                                                    opt.value === allOptionValue
                                                );
                                                
                                                return !hasExistingAllOption;
                                            })(),
                                            allOptionValue: filter.allOptionValue || OptionValues.ALL,
                                            allOptionLabel: (() => {
                                                if (filter.allOptionLabel) return filter.allOptionLabel;
                                                
                                                const allOptionValue = filter.allOptionValue || OptionValues.ALL;
                                                const existingAllOption = filter.filterOptions?.find((opt: any) => 
                                                    opt.value === allOptionValue
                                                );
                                                
                                                return existingAllOption?.label || `All ${filter.dataKey}`;
                                            })(),
                                            maxDisplayItems: filter.maxDisplayItems || 2,
                                            showOnlyAllLabelWhenAllSelected: filter.showOnlyAllLabelWhenAllSelected || false,
                                        }),
                                        ...(filterType !== RevstackFilterTypes.MULTI_SELECT && {
                                            onChange: (value: string) => {
                                                setParentFilterValues(prev => ({
                                                    ...prev,
                                                    [filter.dataKey]: value
                                                }));
                                            },
                                        }),
                                    },
                                };
                                
                                filters.push(filterConfig);
                            });
                        }
            });
        }
        
        return filters;
    }, [widgetConfig, widgetData, parentFilterValues]);

    const getFilteredComponentData = (widgetData: any) => {
        if (!Array.isArray(widgetData)) {
            return widgetData;
        }

        if (parentFilters.length === 0) {
            return widgetData;
        }

        if (!parentFilterValues || Object.keys(parentFilterValues).length === 0) {
            return widgetData;
        }

        // Check if any filter has "get-data" action - if so, skip filtering
        //TODO: Logic for other filter types to be added once we have more screens using them
        const hasGetDataAction = parentFilters.some((filter: any) => {
            return filter?.actions?.onChange === "get-data" || filter?.actions?.onOpenChange === "get-data";
        });

        if (hasGetDataAction) {
            return widgetData;
        }
        
        const hasAllValue = (filterValue: any) => {
            if (Array.isArray(filterValue)) {
                return filterValue.includes(OptionValues.ALL) || filterValue.includes(OptionValues.ALL);
            }
            return filterValue === OptionValues.ALL || filterValue === OptionValues.ALL;
        };
        
        // Helper function to check if an item matches a filter
        const itemMatchesFilter = (item: any, filterKey: string, filterValue: any) => {
            if (Array.isArray(filterValue)) {
                if (filterValue.includes(OptionValues.ALL) || filterValue.includes(OptionValues.ALL)) {
                    return true;
                }
                return filterValue.includes(item[filterKey]);
            }
            if (filterValue === OptionValues.ALL || filterValue === OptionValues.ALL) {
                return true;
            }
            return item[filterKey] === filterValue;
        };
        
        if (values(parentFilterValues).some(hasAllValue)) {
            return widgetData;
        }
        
        // Filter data based on all filter values
        return widgetData.filter((item: any) => {
            return keys(parentFilterValues).every(filterKey => 
                itemMatchesFilter(item, filterKey, parentFilterValues[filterKey])
            );
        });
    };

    const renderWidgetStructure = (
        structure: any,
        keyPrefix: string = ""
    ): any => {
        if (Array.isArray(structure)) {
            const hasOnlySingleWidgets = structure.every(
                (item) =>
                    item &&
                    typeof item === "object" &&
                    item.type &&
                    !Array.isArray(item)
            );

            if (hasOnlySingleWidgets) {
                return (
                    <div
                        // className="bg-white w-100"
                        key={`single-widgets-${keyPrefix}`}
                        style={{ height: "100%" }}
                    >
                        {structure.map((item, index) => {
                            if (item && typeof item === "object" && item.type) {
                                const componentData = item.formatter(
                                    widgetConfig,                                    
                                    item,
                                    widgetData
                                );
                                componentData.data = getFilteredComponentData(componentData?.data);
                                return (
                                    <item.widgetComponent
                                        config={item.config}
                                        key={`single-widget-${keyPrefix}-${index}`}
                                        widgetData={componentData}
                                        widgetConfig={item.config}
                                    />
                                );
                            }
                            return null;
                        })}
                    </div>
                );
            }

            return (
                <Row
                    gutter={[20, 20]}
                    className="bg-white w-100"
                    key={`nested-row-${keyPrefix}`}
                    style={{ height: "100%" }}
                >
                    {structure.map((item, index) => {
                        if (Array.isArray(item)) {
                            // Check if this item contains nested arrays (like [[w1,w2],[w3,w4]])
                            const isNestedGrid = item.some((subItem) =>
                                Array.isArray(subItem)
                            );

                            if (isNestedGrid) {
                                // This is a nested grid - render in a column with 2/3 width
                                return (
                                    <Col
                                        span={16}
                                        key={`nested-grid-${keyPrefix}-${index}`}
                                    >
                                        {item.map((subRow, subIndex) => (
                                            <Row
                                                gutter={[20, 20]}
                                                className={
                                                    subIndex > 0 ? "mt-3" : ""
                                                }
                                                key={`sub-row-${keyPrefix}-${index}-${subIndex}`}
                                            >
                                                {subRow.map(
                                                    (
                                                        widget: any,
                                                        widgetIndex: number
                                                    ) => (
                                                        <Col
                                                            xs={24}
                                                            md={12}
                                                            key={`${widget.type}-${widgetIndex}`}
                                                        >
                                                            <widget.widgetComponent
                                                                title={
                                                                    widget.title
                                                                }
                                                                subTitle={
                                                                    widget?.subTitle ||
                                                                    ""
                                                                }
                                                                infoMessage={
                                                                    widget.infoMessage ||
                                                                    ""
                                                                }
                                                                config={
                                                                    widget.config
                                                                }
                                                                {...widget.formatter(
                                                                    widgetConfig,
                                                                    widget,
                                                                    widgetData
                                                                )}
                                                            />
                                                        </Col>
                                                    )
                                                )}
                                            </Row>
                                        ))}
                                    </Col>
                                );
                            } else {
                                // This is a simple array of widgets - render in a column with 1/3 width
                                return (
                                    <Col
                                        span={8}
                                        className="h-100"
                                        key={`simple-array-${keyPrefix}-${index}`}
                                    >
                                        {item.map(
                                            (
                                                widget: any,
                                                widgetIndex: number
                                            ) => {
                                                const componentData =
                                                    widget.formatter(
                                                        widgetConfig,
                                                        widget,
                                                        widgetData
                                                    );
                                                return (
                                                    <div
                                                        key={`widget-container-${widgetIndex}`}
                                                        className={`h-100 ${
                                                            widgetIndex > 0
                                                                ? "mt-3"
                                                                : ""
                                                        }`}
                                                    >
                                                        <widget.widgetComponent
                                                            title={widget.title}
                                                            subTitle={
                                                                widget?.subTitle ||
                                                                ""
                                                            }
                                                            infoMessage={
                                                                widget.infoMessage ||
                                                                ""
                                                            }
                                                            config={
                                                                widget.config
                                                            }
                                                            {...componentData}
                                                        />
                                                    </div>
                                                );
                                            }
                                        )}
                                    </Col>
                                );
                            }
                        }
                        return null;
                    })}
                </Row>
            );
        }
        return null;
    };

    const renderedWidgets = useMemo(() => {
        if (!widgetConfig?.widgets) return null;
        return renderWidgetStructure(widgetConfig.widgets);
    }, [widgetConfig?.widgets, parentFilterValues, widgetData]);

    const isCardRequired = widgetConfig?.config?.layout?.type === "card";
    const isBodyPaddingReq = widgetConfig?.config?.layout?.isBodyPaddingReq;

    if (isCardRequired) {
        return (
            <WidgetCard
                overwriteDefaultClass={!isCardRequired}
                className={
                    !isCardRequired
                        ? "bg-transparent border-0 rounded-0 shadow-none"
                        : ""
                }
                bodyPaddingClass={isBodyPaddingReq ? "" : "p-0"}
                style={{ height: "100%" }}
            >
                {isCardRequired && (
                    <div
                        className="d-flex align-items-center justify-content-between border-bottom"
                        style={{ padding: 20 }}
                    >
                        <CardHeaderSection
                            title={title || "Channel wise Performance"}
                            titleIcon={widgetConfig?.icon}
                        />
                        {buildWidgetCardFilters({
                            filters: [
                                ...parentFilters,
                                ...(widgetConfig?.config?.isTrendsButtonRequired
                                    ? [
                                        {
                                            section: "" as const,
                                            type: RevstackFilterTypes.TRENDS,
                                            props: {
                                                onClick: () => {
                                                    trendsClickHandler({
                                                        widgetConfig,
                                                        titlePills: [],
                                                        selectedFilterOptions,
                                                        ...widgetConfig?.config?.trendsFiltersConfig,
                                                    });
                                                },
                                            },
                                            isComingSoon: widgetConfig?.config?.isTrendsButtonComingSoon,
                                        },
                                    ]
                                    : []),
                                ...(widgetConfig?.config?.isDeepDiveButtonRequired
                                    ? [
                                        {
                                            section: "" as const,
                                            type: RevstackFilterTypes.DEEP_DIVE,
                                            props: {
                                                onClick: () => {
                                                    // TODO: Implement deep dive functionality
                                                },
                                            },
                                            isComingSoon: true,
                                        },
                                    ]
                                    : []),
                                ...(widgetConfig?.config
                                    ?.isDrillDownButtonRequired
                                    ? [
                                        {
                                            section: "" as const,
                                            type: RevstackFilterTypes.DRILL_DOWN,
                                            props: {
                                                onClick: () => { },
                                                disabled: true,
                                            },
                                            isComingSoon: true,
                                        },
                                    ]
                                    : []),
                            ]
                        })}
                    </div>
                )}
                {renderedWidgets}
            </WidgetCard>
        );
    }

    return <div>{renderedWidgets}</div>;
};

export default RevstackGenericParentContainer;